import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { BookOpen, CreditCard, LayoutDashboard, LogOut, Megaphone, Server, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { UpgradeBanner } from './UpgradeBanner'

export function AppLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggleLang = () => {
    const next = i18n.language === 'ja' ? 'en' : 'ja'
    void i18n.changeLanguage(next)
    localStorage.setItem('i18n-lang', next)
  }

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="flex w-56 flex-col border-r bg-card">
        {/* Brand */}
        <div className="flex h-14 items-center gap-2 px-4">
          <Server className="h-5 w-5 text-primary" />
          <Link to="/dashboard" className="text-sm font-semibold tracking-tight">
            ScenarioMock
          </Link>
        </div>
        <Separator />

        {/* Nav */}
        <nav className="flex-1 space-y-1 p-2">
          <NavLink
            to="/dashboard"
            end
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <LayoutDashboard className="h-4 w-4" />
            {t('nav.projects')}
          </NavLink>
          <a
            href="/gallery/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Megaphone className="h-4 w-4" />
            {t('nav.gallery')}
          </a>
          <NavLink
            to="/docs"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <BookOpen className="h-4 w-4" />
            {t('nav.docs')}
          </NavLink>
          <NavLink
            to="/billing"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <CreditCard className="h-4 w-4" />
            {t('nav.billing')}
          </NavLink>
          <NavLink
            to="/account"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              )
            }
          >
            <Settings className="h-4 w-4" />
            {t('nav.account')}
          </NavLink>
        </nav>

        {/* Pro アップグレードバナー (Free プランのみ) */}
        {user?.plan === 'free' && <UpgradeBanner />}

        <button
          onClick={toggleLang}
          className="mx-3 mb-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {i18n.language === 'ja' ? '🇺🇸 English' : '🇯🇵 日本語'}
        </button>

        <Separator />

        {/* User / Logout */}
        <div className="p-3 space-y-2">
          <p className="truncate px-1 text-xs text-muted-foreground">{user?.email}</p>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-muted-foreground"
            onClick={() => void handleLogout()}
          >
            <LogOut className="h-4 w-4" />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </div>
  )
}
